import React from 'react';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, InputAdornment, Slider, Switch, TextField, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';

/**
 * "Size" card from the Header/Logo modal — controls the dimensions of the
 * header band and whether its background stretches to the viewport edges.
 *
 * value shape:
 *   { logoBarHeight: number,        // px
 *     maxContentWidth: number,      // px
 *     backgroundFullWidth: bool }
 */
const HeaderSizeCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    return (
        <ConfigCard
            title={tr('cfg_header_size_title', 'Size')}
            description={tr('cfg_header_size_desc', 'How tall and wide the logo bar should be.')}
            theme={theme}
        >
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {tr('cfg_header_size_logo_height', 'Logo bar height')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Slider
                        size="small"
                        min={40}
                        max={300}
                        step={5}
                        value={Number(get('logoBarHeight', 100)) || 100}
                        onChange={(_e, v) => set('logoBarHeight')(v)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}px`}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        type="number"
                        size="small"
                        value={get('logoBarHeight', 100) ?? 100}
                        onChange={(e) => set('logoBarHeight')(Math.max(0, parseInt(e.target.value) || 0))}
                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                        sx={{ width: 110 }}
                    />
                </Box>
            </Box>

            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    {tr('cfg_header_size_max_width', 'Maximum content width')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Slider
                        size="small"
                        min={400}
                        max={2400}
                        step={20}
                        value={Number(get('maxContentWidth', 1500)) || 1500}
                        onChange={(_e, v) => set('maxContentWidth')(v)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}px`}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        type="number"
                        size="small"
                        value={get('maxContentWidth', 1500) ?? 1500}
                        onChange={(e) => set('maxContentWidth')(Math.max(0, parseInt(e.target.value) || 0))}
                        InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                        sx={{ width: 110 }}
                    />
                </Box>
            </Box>

            <FormControlLabel
                control={
                    <Switch color="success"
                        size="small"
                        checked={!!get('backgroundFullWidth', true)}
                        onChange={(e) => set('backgroundFullWidth')(e.target.checked)}
                    />
                }
                label={<Typography variant="body2">{tr('cfg_header_size_bg_full', 'Background stretches to viewport edges')}</Typography>}
            />
        </ConfigCard>
    );
};

HeaderSizeCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default HeaderSizeCard;
